import React, { Dispatch, SetStateAction } from 'react'

import { Box, Button, type Colors, Icon, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import useExternalLinks, { EventPayload } from 'modules/onboarding/hooks/useExternalLinks'

const EXCLUDED_LOCALE = 'fr'

type CardProps = {
  title: string
  backgroundColor: Colors
  iconColor: Colors
  icon: React.ComponentProps<typeof Icon.Medium>['icon']
  linkIcon: React.ComponentProps<typeof Icon.Medium>['icon']
  onClick: () => void
}

const Card = ({ title, backgroundColor, iconColor, icon, linkIcon, onClick }: CardProps) => {
  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{ all: 'abs008' }}
      alignItems="center"
      flexDirection="row"
      justifyContent="spaceBetween"
      paddingX="s24"
      paddingY="s16"
      cursor="pointer"
      width="full"
      onClick={onClick}
    >
      <Box flexDirection="row" alignItems="center" gap="s16">
        <Box
          background={backgroundColor}
          borderRadius={{ all: 'abs016' }}
          minHeight="s48"
          minWidth="s48"
          justifyContent="center"
          alignItems="center"
          alignSelf="center"
        >
          <Icon.Medium icon={icon} color={iconColor} />
        </Box>
        <Text.Normal size="300" color="grey1000" weight="medium">
          {title}
        </Text.Normal>
      </Box>
      <Button type="secondary" onClick={onClick} iconName={linkIcon} />
    </Box>
  )
}

type Props = {
  eventPayload: EventPayload
  setIsTutorialsSectionOpen: Dispatch<SetStateAction<boolean>>
}

const Resources = ({ eventPayload, setIsTutorialsSectionOpen }: Props) => {
  const helpCenterLink = helpCenterUrl()
  const {
    access: { locale },
  } = useCurrentSession()
  const { handleExternalClick } = useExternalLinks()

  return (
    <Box gap="s16">
      <Text size="100" color="grey800" weight="bold" caps>
        {i18n.t('help_widget.sidepanel.resources.title')}
      </Text>
      {locale !== EXCLUDED_LOCALE && (
        <Card
          title={i18n.t('help_widget.sidepanel.resources.tutorials')}
          backgroundColor="purple200"
          iconColor="purple1000"
          icon="play"
          linkIcon="arrow-right"
          onClick={() => {
            track(trackingEvents.onboarding.goToVideoTutorials, eventPayload)
            setIsTutorialsSectionOpen(true)
          }}
        />
      )}
      <Card
        title={i18n.t('help_widget.sidepanel.resources.help_center')}
        backgroundColor="radicalRed100"
        iconColor="radicalRed1000"
        icon="question-circle-line"
        linkIcon="external link"
        onClick={() =>
          handleExternalClick(
            trackingEvents.onboarding.goToHelpCenterLink,
            { ...eventPayload, href: helpCenterLink },
            helpCenterLink
          )
        }
      />
    </Box>
  )
}

export default Resources
